import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  Font,
} from '@react-pdf/renderer'
import type { CV, DateFormat, CVLanguage } from '../../types/cv'
import { boldPalettes, type BoldColors } from '../../utils/colorPalettes'
import { getCVLabels, getLevelLabel } from '../../utils/cvTranslations'
import { EmailIcon, PhoneIcon, LocationIcon, BirthDateIcon } from './ContactIcons'

function getColors(paletteId?: string): BoldColors {
  return boldPalettes[paletteId || 'coral'] || boldPalettes.coral
}

// Register fonts for Bold template - using Source Sans Pro (same as CleanTemplate for compatibility)
Font.register({
  family: 'Source Sans Pro',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/sourcesanspro/v22/6xK3dSBYKcSV-LCoeQqfX1RYOo3aPw.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/sourcesanspro/v22/6xKydSBYKcSV-LCoeQqfX1RYOo3i54rAkA.ttf',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/sourcesanspro/v22/6xKydSBYKcSV-LCoeQqfX1RYOo3ig4vAkA.ttf',
      fontWeight: 700,
    },
  ],
})


// Bold color palette
const colors = {
  primary: '#FF6B6B', // Coral
  secondary: '#4F46E5', // Indigo
  dark: '#1F1F1F',
  gray: '#6B7280',
  lightGray: '#F3F4F6',
  white: '#FFFFFF',
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Source Sans Pro',
    fontSize: 10,
    color: colors.dark,
    lineHeight: 1.4,
    paddingTop: 25,
  },
  // Fixed sidebar background - appears on every page, extends into padding area
  sidebarBackground: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '35%',
    backgroundColor: colors.dark,
  },
  contentWrapper: {
    flexDirection: 'row-reverse',
    minHeight: '100%',
  },
  // Left sidebar (30%) - background provided by fixed sidebarBackground
  sidebar: {
    width: '35%',
    padding: 25,
    paddingTop: 10,
  },
  // Main content (70%)
  main: {
    width: '65%',
    padding: 30,
    paddingTop: 10,
    backgroundColor: colors.white,
  },
  // Sidebar styles
  profileSection: {
    marginBottom: 25,
  },
  initials: {
    width: 70,
    height: 70,
    backgroundColor: colors.primary,
    borderRadius: 35,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  initialsText: {
    color: colors.white,
    fontSize: 24,
    fontWeight: 700,
  },
  profilePhoto: {
    width: 70,
    height: 70,
    borderRadius: 35,
    marginBottom: 15,
    objectFit: 'cover',
  },
  sidebarName: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.white,
    marginBottom: 4,
  },
  sidebarTitle: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: 600,
  },
  sidebarSection: {
    marginBottom: 20,
  },
  sidebarSectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#374151',
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    textDecoration: 'none',
  },
  contactIcon: {
    width: 14,
    fontSize: 9,
    marginRight: 8,
    color: colors.primary,
  },
  contactText: {
    fontSize: 9,
    color: '#D1D5DB',
    flex: 1,
  },
  skillTag: {
    backgroundColor: '#374151',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginRight: 5,
    marginBottom: 5,
  },
  skillTagText: {
    color: '#D1D5DB',
    fontSize: 8,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  languageItem: {
    marginBottom: 6,
  },
  languageName: {
    fontSize: 10,
    fontWeight: 600,
    color: colors.white,
  },
  languageLevel: {
    fontSize: 8,
    color: '#9CA3AF',
  },
  // Main content styles
  mainHeader: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  mainName: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.dark,
    marginBottom: 3,
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.secondary,
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: colors.secondary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGray,
  },
  summaryText: {
    fontSize: 10,
    color: colors.gray,
    lineHeight: 1.6,
  },
  entryContainer: {
    marginBottom: 14,
  },
  entryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  entryTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: colors.dark,
  },
  entryCompany: {
    fontSize: 10,
    fontWeight: 600,
    color: colors.primary,
  },
  entryMeta: {
    alignItems: 'flex-end',
  },
  entryDate: {
    fontSize: 9,
    color: colors.gray,
    fontWeight: 600,
  },
  entryLocation: {
    fontSize: 8,
    color: '#9CA3AF',
  },
  entryDescription: {
    fontSize: 9,
    color: colors.gray,
    marginTop: 4,
    lineHeight: 1.5,
  },
})

const localeMap: Record<CVLanguage, string> = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  pt: 'pt-PT',
  de: 'de-DE',
}

function formatDate(
  dateStr: string | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  if (!dateStr) return ''

  const str = dateStr.trim().toLowerCase()
  const locale = localeMap[language] || 'en-US'

  // Handle "present", "actual", "current"
  if (str.includes('present') || str.includes('actual') || str.includes('current')) {
    return presentLabel
  }

  const yearOnly = format === 'year-only'

  // Try YYYY-MM format (e.g., "2024-01")
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-')
    if (yearOnly) return year
    const date = new Date(parseInt(year), parseInt(month) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  // Try YYYY-MM-DD format (e.g., "2024-01-15")
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    if (yearOnly) return dateStr.split('-')[0]
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  // Try just year (e.g., "2024")
  if (/^\d{4}$/.test(dateStr)) {
    return dateStr
  }

  // Try MM/YYYY or MM-YYYY format
  const mmYYYYMatch = dateStr.match(/^(\d{1,2})[/-](\d{4})$/)
  if (mmYYYYMatch) {
    if (yearOnly) return mmYYYYMatch[2]
    const date = new Date(parseInt(mmYYYYMatch[2]), parseInt(mmYYYYMatch[1]) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  // Extract year from string if present (e.g., "Enero 2024" -> "2024")
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/)
  if (yearMatch) {
    return yearMatch[0]
  }

  // If nothing works, return empty string instead of "Invalid Date"
  return ''
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  current: boolean | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  const startStr = formatDate(start, format, language, presentLabel)
  const endStr = current ? presentLabel : formatDate(end, format, language, presentLabel)
  if (!startStr && !endStr) return ''
  if (!startStr) return endStr
  if (!endStr) return startStr
  return `${startStr} - ${endStr}`
}


function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

interface BoldTemplateProps {
  cv: CV
  colorPalette?: string
}


export function BoldTemplate({ cv, colorPalette }: BoldTemplateProps) {
  const paletteColors = getColors(colorPalette)
  const labels = getCVLabels(cv.settings?.language, cv.settings?.customLabels)
  const showIcons = cv.settings?.showContactIcons ?? true
  const hasContact =
    cv.personalInfo?.email ||
    cv.personalInfo?.phone ||
    cv.personalInfo?.location ||
    (cv.personalInfo?.links && cv.personalInfo.links.length > 0)

  const hasSkills =
    (cv.skills?.technical && cv.skills.technical.length > 0) ||
    (cv.skills?.soft && cv.skills.soft.length > 0) ||
    (cv.skills?.tools && cv.skills.tools.length > 0)

  const hasLanguages = cv.languages && cv.languages.some((lang) => lang.name)
  const hasExperience = cv.experience && cv.experience.length > 0
  const hasEducation = cv.education && cv.education.length > 0

  const allSkills = [
    ...(cv.skills?.technical || []),
    ...(cv.skills?.soft || []),
    ...(cv.skills?.tools || []),
  ]

  return (
    <Document
      title={`CV - ${cv.personalInfo?.fullName || 'Curriculum Vitae'}`}
      author={cv.personalInfo?.fullName || ''}
      subject={cv.personalInfo?.title || 'Curriculum Vitae'}
      creator="CVtoday"
      keywords={`CV, resume, ${cv.personalInfo?.fullName || ''}, ${cv.personalInfo?.title || ''}`}
    >
      <Page size="A4" style={styles.page}>
        {/* Fixed sidebar background - repeats on every page */}
        <View style={[styles.sidebarBackground, { backgroundColor: paletteColors.dark }]} fixed />

        {/* Content wrapper - row-reverse so main content is first in DOM (ATS reads it first) but sidebar stays visually on left */}
        <View style={styles.contentWrapper}>
          {/* Main Content - rendered first for ATS reading order */}
          <View style={styles.main}>
          {/* Header */}
          <View style={[styles.mainHeader, { borderBottomColor: paletteColors.primary }]}>
            {cv.personalInfo?.fullName && (
              <Text style={styles.mainName}>{cv.personalInfo.fullName}</Text>
            )}
            {cv.personalInfo?.title && (
              <Text style={[styles.mainTitle, { color: paletteColors.secondary }]}>{cv.personalInfo.title}</Text>
            )}
          </View>

          {/* Summary */}
          {cv.summary && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: paletteColors.secondary }]}>{labels.summary}</Text>
              <Text style={styles.summaryText}>{cv.summary}</Text>
            </View>
          )}

          {/* Experience */}
          {hasExperience && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: paletteColors.secondary }]} minPresenceAhead={40}>{labels.experience}</Text>
              {cv.experience?.map((exp) => (
                <View key={exp.id} style={styles.entryContainer}>
                  <View style={styles.entryHeader}>
                    <View>
                      <Text style={styles.entryTitle}>
                        {exp.position || 'Puesto'}
                      </Text>
                      {exp.company && (
                        <Text style={[styles.entryCompany, { color: paletteColors.primary }]}>{exp.company}</Text>
                      )}
                    </View>
                    <View style={styles.entryMeta}>
                      <Text style={styles.entryDate}>
                        {formatDateRange(exp.startDate, exp.endDate, exp.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                      </Text>
                      {exp.location && (
                        <Text style={styles.entryLocation}>{exp.location}</Text>
                      )}
                    </View>
                  </View>
                  {exp.description && (
                    <Text style={styles.entryDescription}>{exp.description}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Education */}
          {hasEducation && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: paletteColors.secondary }]} minPresenceAhead={40}>{labels.education}</Text>
              {cv.education?.map((edu) => (
                <View key={edu.id} style={styles.entryContainer}>
                  <View style={styles.entryHeader}>
                    <View>
                      <Text style={styles.entryTitle}>
                        {edu.degree}
                        {edu.field && ` en ${edu.field}`}
                      </Text>
                      {edu.institution && (
                        <Text style={[styles.entryCompany, { color: paletteColors.primary }]}>{edu.institution}</Text>
                      )}
                    </View>
                    <View style={styles.entryMeta}>
                      <Text style={styles.entryDate}>
                        {formatDateRange(edu.startDate, edu.endDate, edu.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                      </Text>
                      {edu.location && (
                        <Text style={styles.entryLocation}>{edu.location}</Text>
                      )}
                    </View>
                  </View>
                  {edu.gpa && (
                    <Text style={styles.entryDescription}>{labels.gpa}: {edu.gpa}</Text>
                  )}
                </View>
              ))}
            </View>
          )}
          </View>

          {/* Sidebar - rendered second (ATS reads after main content) */}
          <View style={styles.sidebar}>
          {/* Profile photo/initials only (name/title already in main) */}
          <View style={styles.profileSection}>
            {cv.personalInfo?.photo ? (
              <Image src={cv.personalInfo.photo} style={styles.profilePhoto} />
            ) : (
              cv.personalInfo?.fullName && (
                <View style={[styles.initials, { backgroundColor: paletteColors.primary }]}>
                  <Text style={styles.initialsText}>
                    {getInitials(cv.personalInfo.fullName)}
                  </Text>
                </View>
              )
            )}
          </View>

          {/* Contact */}
          {hasContact && (
            <View style={styles.sidebarSection}>
              <Text style={[styles.sidebarSectionTitle, { color: paletteColors.primary }]}>{labels.contact}</Text>
              {cv.personalInfo?.email && (
                <Link src={`mailto:${cv.personalInfo.email}`} style={styles.contactItem}>
                  {showIcons ? (
                    <EmailIcon size={11} color={paletteColors.primary} style={{ marginRight: 8 }} />
                  ) : (
                    <Text style={[styles.contactIcon, { color: paletteColors.primary }]}>@</Text>
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.email}</Text>
                </Link>
              )}
              {cv.personalInfo?.phone && (
                <Link src={`tel:${cv.personalInfo.phone}`} style={styles.contactItem}>
                  {showIcons ? (
                    <PhoneIcon size={11} color={paletteColors.primary} style={{ marginRight: 8 }} />
                  ) : (
                    <Text style={[styles.contactIcon, { color: paletteColors.primary }]}>#</Text>
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.phone}</Text>
                </Link>
              )}
              {cv.personalInfo?.location && (
                <View style={styles.contactItem}>
                  {showIcons ? (
                    <LocationIcon size={11} color={paletteColors.primary} style={{ marginRight: 8 }} />
                  ) : (
                    <Text style={[styles.contactIcon, { color: paletteColors.primary }]}>*</Text>
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.location}</Text>
                </View>
              )}
              {cv.personalInfo?.birthDate && (
                <View style={styles.contactItem}>
                  {showIcons ? (
                    <BirthDateIcon size={11} color={paletteColors.primary} style={{ marginRight: 8 }} />
                  ) : (
                    <Text style={[styles.contactIcon, { color: paletteColors.primary }]}>+</Text>
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.birthDate}</Text>
                </View>
              )}
              {cv.personalInfo?.links?.map(
                (link) =>
                  link.url ? (
                    <Link key={link.id} src={link.url} style={styles.contactItem}>
                      <Text style={styles.contactIcon}>~</Text>
                      <Text style={styles.contactText}>
                        {link.label || link.url}
                      </Text>
                    </Link>
                  ) : null
              )}
            </View>
          )}

          {/* Skills */}
          {hasSkills && (
            <View style={styles.sidebarSection}>
              <Text style={[styles.sidebarSectionTitle, { color: paletteColors.primary }]}>{labels.skills}</Text>
              <View style={styles.skillsContainer}>
                {allSkills.slice(0, 12).map((skill, index) => (
                  <View key={index} style={styles.skillTag}>
                    <Text style={styles.skillTagText}>{skill}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Languages */}
          {hasLanguages && (
            <View style={styles.sidebarSection}>
              <Text style={[styles.sidebarSectionTitle, { color: paletteColors.primary }]}>{labels.languages}</Text>
              {cv.languages?.map(
                (lang) =>
                  lang.name ? (
                    <View key={lang.id} style={styles.languageItem}>
                      <Text style={styles.languageName}>{lang.name}</Text>
                      {lang.level ? (
                        <Text style={styles.languageLevel}>
                          {getLevelLabel(lang.level, cv.settings?.language)}
                        </Text>
                      ) : null}
                    </View>
                  ) : null
              )}
            </View>
          )}
        </View>
        </View>
      </Page>
    </Document>
  )
}
